import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Filter, Sparkles } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import useLenis from "@/hooks/useLenis";
import api from "@/lib/api";
import { AdCard } from "@/pages/Home";
import { Reveal } from "@/components/site/Reveal";

export default function ShopPage() {
  useLenis();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/content?type=ad&visible=true").then((r) => setItems(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const active = useMemo(() => items.filter((a) => a.active !== false), [items]);
  const categories = useMemo(() => {
    const set = new Set();
    active.forEach((a) => a.category && set.add(a.category));
    return ["all", ...Array.from(set)];
  }, [active]);
  const shown = cat === "all" ? active : active.filter((a) => a.category === cat);

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-text/50 hover:text-imi-gold transition-colors mb-10">
            <ArrowLeft size={16} /> Back home
          </Link>

          <Reveal className="mb-12 max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5 inline-flex items-center gap-2"><ShoppingBag size={12} /> The Store</p>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight">Books, films <span className="italic gold-gradient-text">&amp; more</span>.</h1>
            <p className="mt-6 text-imi-text/60 text-base md:text-lg leading-relaxed">
              Every purchase directly funds survivor-led journalism, film production and policy work. Browse the works our founder, partners and community stand behind.
            </p>
          </Reveal>

          {categories.length > 2 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="shop-filters">
              <Filter size={14} className="text-imi-text/40" />
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`shop-filter-${c}`}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${cat === c ? "border-imi-gold text-imi-gold bg-imi-gold/10" : "border-imi-border text-imi-text/50 hover:text-imi-text hover:border-imi-text/40"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <div className="py-24 text-center border border-imi-border" data-testid="shop-empty">
              <Sparkles className="mx-auto text-imi-gold/40 mb-4" size={32} />
              <div className="text-imi-text/50">Nothing on the shelves just yet. Check back soon.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
              {shown.map((ad, i) => (
                <Reveal key={ad.id} delay={i * 0.04}>
                  <AdCard ad={ad} testIdPrefix="shop-item" />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer settings={settings} />
    </div>
  );
}
